import { Box, Flex, Heading, Text, Table } from "@libs/design";

import { SeverityBadge } from "../../severity-badge";
import { getScrollbarStyles } from "../../../utils";

export type CompanyDrilldown = {
  "Company Code": number | string;
  Domain: string;
  "From Date": string;
  "To Date": string;
  "Expected Days": number;
  "Actual Days": number;
  Delta: number;
  "Missing Dates List": string;
  Severity: string;
};

export type CompanyTableProps = {
  data: CompanyDrilldown[];
  title?: string;
  embedded?: boolean;
};

const FONT_SIZE = 14;

export const CompanyTable = ({
  data,
  title = "Company Data Quality",
  embedded = false,
}: CompanyTableProps) => {
  const colors = {
    headerBg: "#F9FAFB",
    headerText: "#374151",
    rowHover: "#F9FAFB",
    textPrimary: "#111827",
    textSecondary: "#6B7280",
    border: "#E5E7EB",
  };

  const centeredColumns = ["Expected Days", "Actual Days", "Delta"];

  const content = (
    <>
      {/* Header */}
      <Box p={6} borderBottomWidth="1px" style={{ borderColor: colors.border }}>
        <Flex justify="space-between" align="center">
          <Box>
            <Heading fontSize="xl" fontWeight="semibold" color="gray.900" mb={1}>
              {title}
            </Heading>
            <Text fontSize="sm" color="gray.500">
              Detailed overview of data consistency across companies and domains.
            </Text>
          </Box>
        </Flex>
      </Box>

      {/* Table */}
      <Box px={6} py={4}>
        <Box
          borderRadius="md"
          borderWidth="1px"
          overflow="auto"
          maxH="60vh"
          style={{ borderColor: colors.border }}
          css={getScrollbarStyles()}
        >
          <Table.Root
            css={{
              "& th, & td": { padding: "14px 20px" },
              "& th": { borderBottom: `1px solid ${colors.border}` },
            }}
          >
            <Table.Header
              position="sticky"
              top={0}
              zIndex={1}
              style={{ backgroundColor: colors.headerBg }}
            >
              <Table.Row>
                {[
                  "Company Code",
                  "Domain",
                  "From / To Date",
                  "Expected Days",
                  "Actual Days",
                  "Delta",
                  "Missing Dates List",
                  "Severity",
                ].map((col) => (
                  <Table.ColumnHeader
                    key={col}
                    textTransform="uppercase"
                    letterSpacing="wider"
                    style={{
                      color: colors.headerText,
                      fontSize: FONT_SIZE,
                      fontWeight: 600,
                      backgroundColor: colors.headerBg,
                      textAlign: centeredColumns.includes(col) ? "center" : "left",
                    }}
                  >
                    {col}
                  </Table.ColumnHeader>
                ))}
              </Table.Row>
            </Table.Header>

            <Table.Body>
              {data?.map((row, i) => {
                const delta = row["Delta"];
                const deltaColor = delta === 0 ? "#16A34A" : "#DC2626";

                return (
                  <Table.Row
                    key={i}
                    _hover={{ bg: colors.rowHover }}
                    style={{ borderBottom: `1px solid ${colors.border}` }}
                  >
                    <Table.Cell
                      fontWeight="medium"
                      style={{ color: colors.textPrimary, fontSize: FONT_SIZE }}
                    >
                      {row["Company Code"]}
                    </Table.Cell>
                    <Table.Cell
                      style={{
                        color: colors.textSecondary,
                        fontSize: FONT_SIZE,
                      }}
                    >
                      {row["Domain"]}
                    </Table.Cell>
                    <Table.Cell
                      style={{
                        color: colors.textSecondary,
                        fontSize: FONT_SIZE,
                        textWrap: "nowrap",
                      }}
                    >
                      {row["From Date"]} / {row["To Date"]}
                    </Table.Cell>

                    <Table.Cell
                      style={{
                        color: colors.textPrimary,
                        fontSize: FONT_SIZE,
                        fontWeight: 500,
                        textAlign: "center",
                      }}
                    >
                      {row["Expected Days"]}
                    </Table.Cell>

                    <Table.Cell
                      style={{
                        color: colors.textPrimary,
                        fontSize: FONT_SIZE,
                        fontWeight: 500,
                        textAlign: "center",
                      }}
                    >
                      {row["Actual Days"]}
                    </Table.Cell>

                    <Table.Cell
                      fontWeight="semibold"
                      style={{
                        color: deltaColor,
                        fontSize: FONT_SIZE,
                        textAlign: "center",
                      }}
                    >
                      {delta}
                    </Table.Cell>

                    <Table.Cell
                      style={{
                        color: colors.textSecondary,
                        fontSize: FONT_SIZE,
                        maxWidth: 280,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      title={row["Missing Dates List"]}
                    >
                      {row["Missing Dates List"] || "-"}
                    </Table.Cell>

                    <Table.Cell>
                      <SeverityBadge severity={row["Severity"]} />
                    </Table.Cell>
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table.Root>
        </Box>
      </Box>
    </>
  );

  if (embedded) return content;

  return (
    <Box bg="white" borderRadius="xl" borderWidth="1px" boxShadow="sm" overflow="hidden">
      {content}
    </Box>
  );
};
